import { useModal } from '@ebay/nice-modal-react';
import type { VerificationRequest } from 'matrix-js-sdk/src/crypto-api';
import { VerificationPhase, VerificationRequestEvent } from 'matrix-js-sdk/src/crypto-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as Devices } from 'assets/icons/Devices.svg';
import { ReactComponent as MapLocation } from 'assets/icons/MapLocation.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useToast from 'contexts/ToastProvider/useToast';
import NotificationsMobileModal from 'modules/Notifications/NotificationsMobileModal';
import useNotifications from 'modules/Notifications/NotificationsProvider/useNotifications';
import { APP_ROUTES } from 'routes/routePaths';
import logger from 'utils/logging/faro';

import useMatrixDeviceVerificationContext from '../../useMatrixDeviceVerificationContext';
import { sasVerificationId } from '../../utils';

const useSasVerificationNotifications = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { crossSigningStatus, activeRequest, hasIncomingRequest, getLocationFromIp } = useMatrixDeviceContext();
  const { remove: closeNotificationsModal } = useModal(NotificationsMobileModal);
  const { addNotification, removeNotification, notificationsState } = useNotifications();
  const { pushToast, removeToast } = useToast();
  const { acceptIncomingRequest, openIncomingModal } = useMatrixDeviceVerificationContext();
  const navigate = useNavigate();

  const [acceptingDevices, setAcceptingDevices] = useState<Set<string>>(new Set());
  const createdForRequestRef = useRef<VerificationRequest | null>(null);

  const cleanupNotification = useCallback(
    (deviceId: string) => {
      const notificationId = `${sasVerificationId.notification}${deviceId}`;
      const toastId = `${sasVerificationId.toast}${deviceId}`;

      const notificationExists = notificationsState.some((n) => n.id === notificationId);
      if (notificationExists) removeNotification(notificationId);

      removeToast(toastId);

      setAcceptingDevices((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
    },
    [removeNotification, removeToast, notificationsState]
  );

  const handleVerificationClick = useCallback(
    async (deviceId: string) => {
      if (acceptingDevices.has(deviceId)) return;

      if (
        !activeRequest ||
        activeRequest.otherDeviceId !== deviceId ||
        [VerificationPhase.Cancelled, VerificationPhase.Done].includes(activeRequest.phase)
      ) {
        pushToast({
          variant: 'warning',
          title: t('verification.verification_request_expired'),
          description: t('verification.verification_request_expired'),
          autoDismiss: true,
        });
        cleanupNotification(deviceId);
        return;
      }

      setAcceptingDevices((prev) => new Set(prev).add(deviceId));

      try {
        closeNotificationsModal();
        openIncomingModal();

        await acceptIncomingRequest();
      } catch (err) {
        logger.error('Failed to accept device verification request', err);
        pushToast({
          variant: 'warning',
          title: t('verification.failed_to_accept_verification'),
          description: err instanceof Error ? err.message : t('verification.unable_to_accept_verification_request'),
          autoDismiss: true,
        });
        setAcceptingDevices((prev) => {
          const next = new Set(prev);
          next.delete(deviceId);
          return next;
        });
      }
    },
    [
      acceptingDevices,
      activeRequest,
      pushToast,
      cleanupNotification,
      closeNotificationsModal,
      openIncomingModal,
      acceptIncomingRequest,
      t,
    ]
  );

  const createNotification = useCallback(
    async (request: VerificationRequest) => {
      const deviceId = request.otherDeviceId;
      if (!deviceId) return logger.error('No deviceId in verification request, cannot create notification');

      const notificationId = `${sasVerificationId.notification}${deviceId}`;
      const toastId = `${sasVerificationId.toast}${deviceId}`;

      const notificationExists = notificationsState.some((n) => n.id === notificationId);
      if (notificationExists || !matrixClient) return;

      const deviceInfo = await matrixClient.getDevice(deviceId);
      // NOTE: Device might not always have the last IP if user does everything very quickly, because sync is not yet complete
      const deviceIpAddress = deviceInfo.last_seen_ip;
      const deviceName = deviceInfo.display_name;

      const location = await getLocationFromIp(deviceIpAddress);
      const content = (
        <div className="flex flex-col gap-2">
          <span className="flex">
            <p> {deviceName}</p>
          </span>
          <span className="flex">
            <p className="font-medium mr-1">{t('device_id')}:</p>
            <p> {deviceId}</p>
          </span>
          {deviceIpAddress && (
            <span className="flex">
              <p className="font-medium mr-1">{t('verification.ip_address')}:</p>
              <p> {deviceIpAddress}</p>
            </span>
          )}
          {location && (
            <div className="flex items-center gap-2">
              <MapLocation className="w-7 h-7 stroke-primary-50 stroke-1.5 self-start" />
              {location}
            </div>
          )}
        </div>
      );

      addNotification({
        id: notificationId,
        icon: Devices,
        variant: 'alert',
        title: t('verification.verification_request'),
        content,
        isRead: false,
        timestamp: Date.now(),
        onClick: async () => handleVerificationClick(deviceId),
        onRead: async () => {
          logger.info('Device verification notification read', { requestingFrom: deviceId });
        },
      });

      const pathname = window.location.pathname;
      const isOnMainRoutes =
        pathname === '/' ||
        [APP_ROUTES.meetings, APP_ROUTES.messenger, APP_ROUTES.calendar].some((route) => pathname.startsWith(route));
      if (!isOnMainRoutes) {
        pushToast({
          id: toastId,
          variant: 'warning',
          title: t('verification.new_device_verification_request'),
          description: t('verification.new_device_verification_request_description'),
          actionText: t('verification.go_to_verify'),
          onAction: () => {
            navigate('/');
            removeToast(toastId);
          },
          autoDismiss: false,
        });
      }
    },
    [
      matrixClient,
      addNotification,
      pushToast,
      removeToast,
      navigate,
      notificationsState,
      getLocationFromIp,
      t,
      handleVerificationClick,
    ]
  );

  useEffect(() => {
    if (
      crossSigningStatus !== CrossSigningStatusEnum.VERIFIED ||
      !hasIncomingRequest ||
      !activeRequest ||
      activeRequest.initiatedByMe
    ) {
      return;
    }

    const deviceId = activeRequest.otherDeviceId;
    if (!deviceId) return;

    const isRequestFinished = [VerificationPhase.Done, VerificationPhase.Cancelled].includes(activeRequest.phase);
    const isRequestStarted = activeRequest.phase >= VerificationPhase.Started;
    const isRequestNotReady = ![VerificationPhase.Requested, VerificationPhase.Ready].includes(activeRequest.phase);
    const isSameRequest = createdForRequestRef.current === activeRequest;
    if (isRequestFinished || isRequestStarted || isRequestNotReady || isSameRequest) return;

    createdForRequestRef.current = activeRequest;
    createNotification(activeRequest);
  }, [crossSigningStatus, hasIncomingRequest, activeRequest, createNotification, notificationsState]);

  useEffect(() => {
    if (!activeRequest || !activeRequest.otherDeviceId) return;

    const deviceId = activeRequest.otherDeviceId;

    const onChange = () => {
      if ([VerificationPhase.Done, VerificationPhase.Cancelled].includes(activeRequest.phase)) {
        createdForRequestRef.current = null;
        cleanupNotification(deviceId);
      }
    };

    activeRequest.on(VerificationRequestEvent.Change, onChange);

    return () => {
      activeRequest.off(VerificationRequestEvent.Change, onChange);
    };
  }, [activeRequest, cleanupNotification]);
};

export default useSasVerificationNotifications;
